import React from 'react'
import ReportWrapper from '../ReportWrapper'
import ApplicationOverview from './ApplicationOverview'
import SpecialPeriodAnalysisChart from './SpecialPeriodAnalysisChart'
import ReportSectionHeading from '../ReportSectionHeading'
import { Table, TableData, Text } from '@mantine/core'
import { useTheme } from '../../../components/ThemeProvider'
import { useViewMode } from '../ViewModeProvider'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { EChartsOption } from 'echarts'

echarts.use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent, CanvasRenderer])

interface Rongdata3CLeasingRiskInformationProps {
  data: any
  title: string
  sectionIndex?: string
}

export default function Rongdata3CLeasingRiskInformation({
  data,
  title,
  sectionIndex,
}: Rongdata3CLeasingRiskInformationProps) {
  const theme = useTheme()
  const { viewMode } = useViewMode()

  const leasingData = data?.['3c_leasing'] || {}

  const periods = [
    {
      label: '近3天',
      idAllnum: leasingData?.d3_id_allnum ?? 0,
      idOrgnum: leasingData?.d3_id_orgnum ?? 0,
      cellAllnum: leasingData?.d3_cell_allnum ?? 0,
      cellOrgnum: leasingData?.d3_cell_orgnum ?? 0,
    },
    {
      label: '近7天',
      idAllnum: leasingData?.d7_id_allnum ?? 0,
      idOrgnum: leasingData?.d7_id_orgnum ?? 0,
      cellAllnum: leasingData?.d7_cell_allnum ?? 0,
      cellOrgnum: leasingData?.d7_cell_orgnum ?? 0,
    },
    {
      label: '近14天',
      idAllnum: leasingData?.d14_id_allnum ?? 0,
      idOrgnum: leasingData?.d14_id_orgnum ?? 0,
      cellAllnum: leasingData?.d14_cell_allnum ?? 0,
      cellOrgnum: leasingData?.d14_cell_orgnum ?? 0,
    },
    {
      label: '近1月',
      idAllnum: leasingData?.m1_id_allnum ?? 0,
      idOrgnum: leasingData?.m1_id_orgnum ?? 0,
      cellAllnum: leasingData?.m1_cell_allnum ?? 0,
      cellOrgnum: leasingData?.m1_cell_orgnum ?? 0,
    },
    {
      label: '近3月',
      idAllnum: leasingData?.m3_id_allnum ?? 0,
      idOrgnum: leasingData?.m3_id_orgnum ?? 0,
      cellAllnum: leasingData?.m3_cell_allnum ?? 0,
      cellOrgnum: leasingData?.m3_cell_orgnum ?? 0,
    },
    {
      label: '近6月',
      idAllnum: leasingData?.m6_id_allnum ?? 0,
      idOrgnum: leasingData?.m6_id_orgnum ?? 0,
      cellAllnum: leasingData?.m6_cell_allnum ?? 0,
      cellOrgnum: leasingData?.m6_cell_orgnum ?? 0,
    },
    {
      label: '近12月',
      idAllnum: leasingData?.m12_id_allnum ?? 0,
      idOrgnum: leasingData?.m12_id_orgnum ?? 0,
      cellAllnum: leasingData?.m12_cell_allnum ?? 0,
      cellOrgnum: leasingData?.m12_cell_orgnum ?? 0,
    },
  ]

  const tableData: TableData = {
    head: ['统计周期', '身份证申请次数', '身份证机构数', '手机号申请次数', '手机号机构数'],
    body: periods.map((period) => [
      period.label,
      period.idAllnum,
      period.idOrgnum,
      period.cellAllnum,
      period.cellOrgnum,
    ]),
  }

  const chartOption: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    legend: {
      data: ['身份证申请次数', '手机号申请次数'],
      bottom: 0,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: periods.map((p) => p.label),
      axisLabel: {
        fontSize: 10,
        rotate: 0,
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
    },
    series: [
      {
        name: '身份证申请次数',
        type: 'bar',
        itemStyle: {
          color: theme?.primaryColor || '#3B82F6',
        },
        label: {
          show: true,
          position: 'top',
          fontSize: 10,
          color: '#666',
        },
        data: periods.map((p) => p.idAllnum),
      },
      {
        name: '手机号申请次数',
        type: 'bar',
        itemStyle: {
          color: '#60A5FA',
        },
        label: {
          show: true,
          position: 'top',
          fontSize: 10,
          color: '#666',
        },
        data: periods.map((p) => p.cellAllnum),
      },
    ],
  }

  return (
    <ReportWrapper>
      <ReportSectionHeading title={title} index={sectionIndex} />
      <ApplicationOverview data={leasingData} />
      <div className="mt-2">
        <Text size="sm" fw={600} style={{ color: theme.accentTextColor }}>
          申请明细
        </Text>
        {viewMode === 'table' ? (
          <div className="mt-3">
            <Table
              data={tableData}
              highlightOnHover
              verticalSpacing="sm"
              styles={{
                table: {
                  fontSize: '13px',
                },
                th: {
                  backgroundColor: theme.surfaceMutedColor,
                  color: theme.accentTextColor,
                },
              }}
            />
          </div>
        ) : (
          <div className="mt-3">
            <ReactEChartsCore echarts={echarts} option={chartOption} style={{ height: '300px' }} />
          </div>
        )}
      </div>
      <SpecialPeriodAnalysisChart data={leasingData} />
    </ReportWrapper>
  )
}
